import type { Exchange, Operation } from "@urql/core"
import { pipe, tap } from "wonka"

/** Exchange factory that reexecutes operations after a user reconnects to the internet.
 *
 * @returns a new reconnect {@link Exchange}.
 *
 * @remarks
 * The `reconnectExchange` will reexecute `Operation`s with the `network-only`
 * policy when a user comes back online after losing their connection.
 *
 * The `cache-and-network` policy will always refetch data, but will
 * only refetch queries that are currently active.
 */
export const reconnectExchange = (): Exchange => {
  return ({ client, forward }) =>
    ops$ => {
      if (typeof window === "undefined") {
        return forward(ops$)
      }

      const watchedOperations = new Map<number, Operation>()
      const observedOperations = new Map<number, number>()

      // Listen for the 'online' event to detect when the user reconnects
      window.addEventListener("online", () => {
        setTimeout(() => {
          watchedOperations.forEach(op => {
            client.reexecuteOperation(
              client.createRequestOperation(op.kind, op, {
                ...op.context,
                requestPolicy: "cache-and-network",
              }),
            )
          })
          // Small delay after reconnecting to ensure the user is actually online.
        }, 1000)
      })

      const processIncomingOperation = (op: Operation) => {
        if (
          (op.kind === "query" || op.kind === "subscription") &&
          !observedOperations.has(op.key)
        ) {
          // The number here doesn't matter, it's acting as a Set.
          // Kept as Map as urql also used a Map to keep it consistent with the watchedOperations map I assume.
          observedOperations.set(op.key, 1)
          watchedOperations.set(op.key, op)
        }

        if (op.kind === "teardown" && observedOperations.has(op.key)) {
          observedOperations.delete(op.key)
          watchedOperations.delete(op.key)
        }
      }

      return forward(pipe(ops$, tap(processIncomingOperation)))
    }
}
